import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";

export type MaintenanceVerdict =
    | "actively-maintained"
    | "maintained"
    | "stale"
    | "possibly-abandoned";

export type RatingConfidence = "low" | "medium" | "high";

export interface PluginMaintenanceInsights {
    score: number;
    verdict: MaintenanceVerdict;
    daysSinceUpdate: number | null;
    supportResolutionRate: number | null;
    ratingConfidence: RatingConfidence;
}

const clamp = (value: number, min: number, max: number): number => {
    if (value < min) return min;
    if (value > max) return max;
    return value;
};

const safeRatio = (numerator?: number | null, denominator?: number | null): number | null => {
    if (numerator === null || numerator === undefined) return null;
    if (denominator === null || denominator === undefined) return null;
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) return null;
    if (denominator <= 0) return null;
    return numerator / denominator;
};

const daysSince = (date?: Date | null, nowMs?: number): number | null => {
    if (!date) return null;
    const referenceMs = typeof nowMs === "number" && Number.isFinite(nowMs) ? nowMs : Date.now();
    const diffMs = referenceMs - date.getTime();
    if (!Number.isFinite(diffMs)) return null;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return clamp(days, 0, Number.MAX_SAFE_INTEGER);
};

const recencyScoreFromDays = (days: number): number => {
    if (days <= 7) return 100;
    if (days <= 30) return 90;
    if (days <= 90) return 75;
    if (days <= 180) return 60;
    if (days <= 365) return 40;
    if (days <= 730) return 20;
    return 10;
};

const ratingConfidenceFromCount = (count: number): RatingConfidence => {
    if (count >= 1000) return "high";
    if (count >= 100) return "medium";
    return "low";
};

const ratingDepthScoreFromCount = (count: number): number => {
    if (count >= 5000) return 100;
    if (count >= 1000) return 90;
    if (count >= 250) return 75;
    if (count >= 100) return 65;
    if (count >= 25) return 50;
    if (count >= 10) return 40;
    if (count >= 1) return 30;
    return 20;
};

const verdictFromDays = (days: number): MaintenanceVerdict => {
    if (days <= 90) return "actively-maintained";
    if (days <= 180) return "maintained";
    if (days <= 365) return "stale";
    return "possibly-abandoned";
};

export const getPluginMaintenanceInsights = (
    plugin: Pick<IProcessedPluginData, "last_updated" | "support_threads" | "support_threads_resolved" | "num_ratings">,
    nowMs?: number,
): PluginMaintenanceInsights => {
    const daysSinceUpdate = daysSince(plugin.last_updated, nowMs);
    const normalizedDaysSinceUpdate = daysSinceUpdate ?? 0;
    const recencyScore = recencyScoreFromDays(normalizedDaysSinceUpdate);

    const supportResolutionRate = safeRatio(plugin.support_threads_resolved, plugin.support_threads);
    const supportScore = supportResolutionRate === null
        ? 50
        : clamp(Math.round(supportResolutionRate * 100), 0, 100);

    const ratingsCount = plugin.num_ratings ?? 0;
    const ratingDepthScore = ratingDepthScoreFromCount(ratingsCount);
    const ratingConfidence = ratingConfidenceFromCount(ratingsCount);

    const combinedScore = Math.round(
        recencyScore * 0.6
        + supportScore * 0.2
        + ratingDepthScore * 0.2
    );

    return {
        score: clamp(combinedScore, 0, 100),
        verdict: verdictFromDays(normalizedDaysSinceUpdate),
        daysSinceUpdate,
        supportResolutionRate,
        ratingConfidence,
    };
};
